import { Container } from '@mui/material';
import { isNil } from 'lodash-es';
import { useCallback, useMemo, useState } from 'react';
import { Outlet } from 'react-router';

import Loading from '@/components/elements/Loading';
import ContextHeaderContent from '@/components/layout/dashboard/contextHeader/ContextHeaderContent';
import {
  useDashboardBreadcrumbs,
  useEnrollmentBreadcrumbConfig,
} from '@/components/layout/dashboard/contextHeader/useDashboardBreadcrumbs';
import DashboardContentContainer from '@/components/layout/dashboard/DashboardContentContainer';
import SideNavMenu from '@/components/layout/dashboard/sideNav/SideNavMenu';
import NotFound from '@/components/pages/NotFound';

import { useDashboardState } from '@/hooks/useDashboardState';
import useIsPrintView from '@/hooks/useIsPrintView';
import useSafeParams from '@/hooks/useSafeParams';
import ClientPrintHeader from '@/modules/client/components/ClientPrintHeader';
import EnrollmentNavHeader from '@/modules/enrollment/components/EnrollmentNavHeader';
import { useDetailedEnrollment } from '@/modules/enrollment/hooks/useDetailedEnrollment';
import { useEnrollmentDashboardNavItems } from '@/modules/enrollment/hooks/useEnrollmentDashboardNavItems';
import { DashboardEnrollment } from '@/modules/hmis/types';
import LocalVersionCoordinationPrompt from '@/modules/localVersionCoordination/components/LocalVersionCoordinationPrompt';
import { ProjectDashboardContext } from '@/modules/projects/components/ProjectDashboard';
import {
  DataCollectionFeature,
  DataCollectionFeatureRole,
  EnrolledClientFieldsFragment,
} from '@/types/gqlTypes';

const EnrollmentDashboard: React.FC = () => {
  const params = useSafeParams() as {
    clientId: string;
    enrollmentId: string;
    formRole?: string;
  };
  const isPrint = useIsPrintView();

  // allow pages to "override" the default breadcrumb text
  const [breadcrumbOverrides, overrideBreadcrumbTitles] = useState<
    Record<string, string> | undefined
  >();

  const { enrollment, loading } = useDetailedEnrollment(params.enrollmentId);
  const client = enrollment?.client;

  const enabledFeatures = useMemo(
    () => enrollment?.dataCollectionFeatures.map((f) => f.role) || [],
    [enrollment]
  );

  const getEnrollmentFeature = useCallback(
    (role: DataCollectionFeatureRole) =>
      enrollment?.dataCollectionFeatures.find((f) => f.role === role),
    [enrollment]
  );

  const navItems = useEnrollmentDashboardNavItems(enabledFeatures);

  const { currentPath, focusMode, handleOpenDesktopMenu, ...dashboardState } =
    useDashboardState();

  const outletContext: EnrollmentDashboardContext | undefined = useMemo(
    () =>
      client && enrollment
        ? {
            client,
            overrideBreadcrumbTitles,
            enrollment,
            enrollmentLoading: loading,
            getEnrollmentFeature,
            handleOpenDesktopMenu,
          }
        : undefined,
    [client, enrollment, loading, getEnrollmentFeature, handleOpenDesktopMenu]
  );

  const breadCrumbConfig = useEnrollmentBreadcrumbConfig(outletContext);
  const breadcrumbs = useDashboardBreadcrumbs(
    breadCrumbConfig,
    breadcrumbOverrides
  );

  if (loading && !enrollment) return <Loading />;
  if (!enrollment || !client || !outletContext) return <NotFound />;
  if (enrollment && enrollment.client.id !== params.clientId) {
    return <NotFound />;
  }

  if (isPrint) {
    return (
      <>
        <ClientPrintHeader client={client} enrollment={enrollment} />
        <Outlet context={outletContext} />
      </>
    );
  }

  return (
    <DashboardContentContainer
      navHeader={<EnrollmentNavHeader enrollment={enrollment} />}
      sidebar={
        <SideNavMenu
          items={navItems}
          access={enrollment.access}
          pathParams={{
            clientId: enrollment.client.id,
            enrollmentId: enrollment.id,
          }}
        />
      }
      contextHeader={<ContextHeaderContent breadcrumbs={breadcrumbs} />}
      navLabel='Enrollment'
      {...dashboardState}
      focusMode={focusMode}
      handleOpenDesktopMenu={handleOpenDesktopMenu}
    >
      <Container maxWidth='xl' disableGutters>
        <LocalVersionCoordinationPrompt
          recordType='Enrollment'
          recordId={enrollment.id}
          currentVersion={enrollment.lockVersion}
        />
        <LocalVersionCoordinationPrompt
          recordType='Client'
          recordId={client.id}
          currentVersion={client.lockVersion}
        />
        <Outlet context={outletContext} />
      </Container>
    </DashboardContentContainer>
  );
};

export type EnrollmentDashboardContext = {
  client: EnrolledClientFieldsFragment;
  enrollment?: DashboardEnrollment;
  enrollmentLoading?: boolean; // this would indicate a re-loading, not the initial load
  overrideBreadcrumbTitles: (crumbs: any) => void;
  getEnrollmentFeature: (
    role: DataCollectionFeatureRole
  ) => DataCollectionFeature | void;
  handleOpenDesktopMenu: VoidFunction;
};

export function isEnrollmentDashboardContext(
  value: EnrollmentDashboardContext | ProjectDashboardContext
): value is EnrollmentDashboardContext {
  return (
    !isNil(value) &&
    typeof value === 'object' &&
    !!value.hasOwnProperty('client') &&
    !!value.hasOwnProperty('enrollment')
  );
}

export default EnrollmentDashboard;
